import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Loader2, Layers, Eye, Package, ArrowLeft, Heart, Camera, ScanSearch, Upload, X, Image as ImageIcon, User } from 'lucide-react';
import { getPublicFabricById, getPublicFabricFilterOptions } from '../api/product.api';
import {
  getAllPublicCollections,
  getPublicFabricsByCollection,
  getCollectionByFabric
} from '../api/fabricCollection.api';
import { uploadImageToS3, compressImage } from '../utils/s3Upload';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';
import FabricCatalogueView from './FabricCatalogueView';
import { SESSION_OPEN_PUBLIC_FABRIC_ID } from '../constants/publicFabricNavigation';
import { getMarketplaceHistoryUrl } from '../utils/publicRoutes';

function pushMarketplaceHistory(marketplaceSubView, extra = {}) {
  window.history.pushState(
    { view: 'fabricmarketplace', marketplaceSubView, ...extra },
    '',
    getMarketplaceHistoryUrl()
  );
}

// Lazy Image Component with IntersectionObserver (thumbnail first, original on error — matches My Fabrics)
const LazyImage = ({ src, alt, className, onError, fallbackSrc, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.01
      }
    );

    observer.observe(img);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setUseFallback(false);
    setHasError(false);
    setIsLoaded(false);
  }, [src, fallbackSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = (e) => {
    if (!useFallback && fallbackSrc && fallbackSrc !== src) {
      setUseFallback(true);
      setIsLoaded(false);
      return;
    }
    setHasError(true);
    setIsLoaded(true);
    if (onError) {
      onError(e);
    }
  };

  const activeSrc = useFallback ? fallbackSrc : src;

  return (
    <div ref={imgRef} className="relative w-full h-full">
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center z-0">
          <Loader2 size={24} className="text-gray-400 animate-spin" />
        </div>
      )}

      {isInView && (
        <img
          src={activeSrc}
          alt={alt}
          className={`${className} transition-opacity duration-300 relative z-10 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          {...props}
        />
      )}

      {hasError && isInView && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-20">
          <div className="text-center p-2">
            <Layers size={24} className="text-gray-400 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Image unavailable</p>
          </div>
        </div>
      )}
    </div>
  );
};

const INITIAL_FILTERS = {
  fabricType: '',
  weaveOrKnit: '',
  opacity: '',
  stretch: '',
  color: ''
};

const COLOR_NAME_TO_HEX = {
  red: '#ef4444',
  orange: '#f97316',
  yellow: '#facc15',
  green: '#22c55e',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  purple: '#a855f7',
  pink: '#ec4899',
  gray: '#6b7280',
  grey: '#6b7280',
  black: '#111827',
  white: '#f9fafb',
  brown: '#92400e'
};

const getColorHex = (value) => {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  return COLOR_NAME_TO_HEX[v] || null;
};

const SEARCH_DEBOUNCE_MS = 500;

const getFabricStableId = (fabric) => {
  if (!fabric || typeof fabric !== 'object') return '';
  const raw = fabric._id ?? fabric.id;
  return raw != null ? String(raw) : '';
};

/** Keeps first occurrence; prevents duplicate React keys when API/pagination returns the same product twice. */
const dedupeFabricsById = (list) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list.filter((f) => {
    const id = getFabricStableId(f);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const getCollectionImage = (collection) => {
  if (collection?.image?.url) return collection.image.url;
  if (collection?.image?.s3Url) return collection.image.s3Url;
  if (collection?.fabrics?.length > 0) {
    const first = collection.fabrics[0];
    return first.image || first.images?.original?.s3Url || first.images?.original?.url;
  }
  return null;
};

const getCollectionFabricCount = (collection) => {
  if (!collection?.fabrics) return 0;
  return collection.fabrics.filter((f) => f && f.isPublic !== false).length;
};

const matchesFabricFilters = (fabric, filters, searchQuery) => {
  const q = (searchQuery || '').trim().toLowerCase();
  if (q) {
    const haystack = [
      fabric?.name,
      fabric?.description,
      fabric?.category,
      ...(Array.isArray(fabric?.tags) ? fabric.tags : [])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(q)) return false;
  }

  const analysis = fabric?.fabricAnalysis?.fabric_analysis || fabric?.fabricAnalysis || {};
  const visual = fabric?.fabricAnalysis?.visual_characteristics || analysis?.visual_characteristics || {};

  if (filters.fabricType) {
    const v = visual?.visual_type || analysis?.visual_type || '';
    if (String(v).toLowerCase() !== String(filters.fabricType).toLowerCase()) return false;
  }
  if (filters.weaveOrKnit) {
    const v = analysis?.weave_or_knit || analysis?.weaveOrKnit || '';
    if (String(v).toLowerCase() !== String(filters.weaveOrKnit).toLowerCase()) return false;
  }
  if (filters.opacity) {
    const v = analysis?.opacity || '';
    if (String(v).toLowerCase() !== String(filters.opacity).toLowerCase()) return false;
  }
  if (filters.stretch) {
    const v = analysis?.stretch || '';
    if (String(v).toLowerCase() !== String(filters.stretch).toLowerCase()) return false;
  }
  if (filters.color) {
    const colors = [
      ...(visual?.primary_colors || []),
      ...(visual?.secondary_colors || []),
      ...(fabric?.colorVariants || []).map((c) => c?.colorName || c?.name || c?.hex)
    ]
      .filter(Boolean)
      .map((c) => String(c).toLowerCase());
    if (!colors.some((c) => c.includes(String(filters.color).toLowerCase()))) return false;
  }

  return true;
};

function mergeVisualSearchScores(products, visualScores, isVisualSearch) {
  const list = Array.isArray(products) ? products : [];
  if (!isVisualSearch || !Array.isArray(visualScores)) {
    return list.map(({ visualMatchScore: _drop, ...rest }) => rest);
  }
  return list.map((p, i) => {
    const { visualMatchScore: _prev, ...base } = p;
    const raw = visualScores[i];
    if (raw == null) return base;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) return base;
    return { ...base, visualMatchScore: n };
  });
}

const PublicFabricsGalleryView = ({ setActiveView, setProductDetails, setPreSelectedProduct, showNotification, onOpenLoginModal }) => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [fabrics, setFabrics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [visualSearchImageUrl, setVisualSearchImageUrl] = useState('');
  const [visualSearchImagePreview, setVisualSearchImagePreview] = useState('');
  const [isVisualSearchUploading, setIsVisualSearchUploading] = useState(false);
  const [showImageSearchOptions, setShowImageSearchOptions] = useState(false);
  const [isCameraCaptureOpen, setIsCameraCaptureOpen] = useState(false);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterDraft, setFilterDraft] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [filterOptions, setFilterOptions] = useState({
    fabricTypes: [],
    weaveKnit: [],
    opacity: [],
    stretch: [],
    colors: []
  });
  const [selectedFabric, setSelectedFabric] = useState(null);
  const observerTarget = useRef(null);
  const searchTimeoutRef = useRef(null);
  const visualSearchInputRef = useRef(null);
  const visualSearchCameraInputRef = useRef(null);
  const imageSearchMenuRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraCanvasRef = useRef(null);
  const fetchingRef = useRef(false);
  const prefetchPageRef = useRef(null);
  const isHandlingPopState = useRef(false);
  const appliedFilterCount = Object.values(filters).filter(Boolean).length;
  const inCollectionView = Boolean(selectedCollection);

  const displayedFabrics = useMemo(() => {
    if (!inCollectionView) return [];
    return dedupeFabricsById(
      fabrics.filter((f) => matchesFabricFilters(f, filters, searchQuery))
    );
  }, [inCollectionView, fabrics, filters, searchQuery]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const data = await getPublicFabricFilterOptions();
        setFilterOptions({
          fabricTypes: Array.isArray(data?.fabricTypes) ? data.fabricTypes : [],
          weaveKnit: Array.isArray(data?.weaveKnit) ? data.weaveKnit : [],
          opacity: Array.isArray(data?.opacity) ? data.opacity : [],
          stretch: Array.isArray(data?.stretch) ? data.stretch : [],
          colors: Array.isArray(data?.colors) ? data.colors : []
        });
      } catch (err) {
        console.warn('Failed to load public filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Visual search handlers
  const clearVisualSearch = useCallback(() => {
    if (visualSearchImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(visualSearchImagePreview);
    }
    setVisualSearchImagePreview('');
    setVisualSearchImageUrl('');
    if (visualSearchInputRef.current) {
      visualSearchInputRef.current.value = '';
    }
    if (visualSearchCameraInputRef.current) {
      visualSearchCameraInputRef.current.value = '';
    }
  }, [visualSearchImagePreview]);

  const processVisualSearchFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      showNotification?.('Please select an image file', 'error');
      return;
    }

    setIsVisualSearchUploading(true);
    let previewUrl = null;
    try {
      const compressedFile = await compressImage(file, { maxWidth: 1280, maxHeight: 1280, quality: 0.85 });
      previewUrl = URL.createObjectURL(compressedFile);
      setVisualSearchImagePreview(prev => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return previewUrl;
      });

      const uploadResult = await uploadImageToS3(compressedFile, {
        folder: 'visual-search',
        fileName: `visual-search-${Date.now()}-${compressedFile.name}`
      });

      const uploadedUrl = uploadResult?.url || '';
      if (!uploadedUrl) throw new Error('Failed to upload search image');

      setSearchInput('');
      setSearchQuery('');
      setVisualSearchImageUrl(uploadedUrl);
      showNotification?.('Image search enabled', 'success');
    } catch (err) {
      console.error('Error preparing visual search image:', err);
      if (previewUrl?.startsWith?.('blob:')) URL.revokeObjectURL(previewUrl);
      showNotification?.(err?.message || 'Failed to process search image', 'error');
      clearVisualSearch();
    } finally {
      setIsVisualSearchUploading(false);
      if (visualSearchInputRef.current) visualSearchInputRef.current.value = '';
      if (visualSearchCameraInputRef.current) visualSearchCameraInputRef.current.value = '';
    }
  }, [showNotification, clearVisualSearch]);

  const handleVisualSearchImageSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    await processVisualSearchFile(file);
  }, [processVisualSearchFile]);

  const handleVisualSearchCameraCapture = useCallback(async (e) => {
    const file = e.target.files?.[0];
    await processVisualSearchFile(file);
  }, [processVisualSearchFile]);

  const closeCameraCapture = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    setCameraStream(null);
    setIsCameraCaptureOpen(false);
    setIsCameraStarting(false);
  }, [cameraStream]);

  const openCameraCapture = useCallback(async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      visualSearchCameraInputRef.current?.click();
      return;
    }
    setIsCameraStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      setCameraStream(stream);
      setIsCameraCaptureOpen(true);
    } catch (err) {
      console.error('Camera access failed:', err);
      showNotification?.('Camera unavailable, opening file picker instead', 'info');
      visualSearchCameraInputRef.current?.click();
    } finally {
      setIsCameraStarting(false);
    }
  }, [showNotification]);

  const captureCameraPhoto = useCallback(async () => {
    if (!cameraVideoRef.current || !cameraCanvasRef.current) {
      showNotification?.('Camera is not ready yet', 'error');
      return;
    }
    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      showNotification?.('Failed to capture image', 'error');
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
    });
    if (!blob) {
      showNotification?.('Failed to capture photo', 'error');
      return;
    }
    const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
    closeCameraCapture();
    await processVisualSearchFile(file);
  }, [showNotification, closeCameraCapture, processVisualSearchFile]);

  const openImageSearchOption = useCallback((mode) => {
    setShowImageSearchOptions(false);
    if (mode === 'camera') {
      openCameraCapture();
      return;
    }
    visualSearchInputRef.current?.click();
  }, [openCameraCapture]);

  useEffect(() => {
    if (!showImageSearchOptions) return;
    const handleOutsideClick = (e) => {
      if (imageSearchMenuRef.current && !imageSearchMenuRef.current.contains(e.target)) {
        setShowImageSearchOptions(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showImageSearchOptions]);

  useEffect(() => {
    if (!cameraVideoRef.current || !cameraStream) return;
    cameraVideoRef.current.srcObject = cameraStream;
  }, [cameraStream]);

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  useEffect(() => {
    return () => {
      if (visualSearchImagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(visualSearchImagePreview);
      }
    };
  }, [visualSearchImagePreview]);

  const fetchCollections = useCallback(async (page = 1, append = false) => {
    if (fetchingRef.current && append) return;

    fetchingRef.current = true;

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null);
    }

    try {
      const trimmedSearchQuery = (searchQuery || '').trim();
      const response = await getAllPublicCollections(page, 24, trimmedSearchQuery);
      const { collections: fetchedCollections, pagination } = response || {};

      if (append) {
        setCollections((prev) => {
          const seen = new Set(prev.map((c) => String(c._id)));
          const next = (fetchedCollections || []).filter((c) => c?._id && !seen.has(String(c._id)));
          return [...prev, ...next];
        });
      } else {
        setCollections(fetchedCollections || []);
      }

      setCurrentPage(pagination?.currentPage ?? 1);
      setHasNextPage(Boolean(pagination?.hasNextPage));

      if (pagination?.hasNextPage && !append) {
        const nextPage = page + 1;
        setTimeout(() => {
          getAllPublicCollections(nextPage, 24, trimmedSearchQuery)
            .then((prefetchResponse) => {
              prefetchPageRef.current = prefetchResponse;
            })
            .catch((err) => {
              console.warn('Prefetch failed:', err);
            });
        }, 500);
      }
    } catch (error) {
      console.error('Error fetching public collections:', error);
      setError(error.response?.data?.message || 'Failed to load fabric collections');
      if (!append) {
        setCollections([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [searchQuery]);

  const loadCollectionFabrics = useCallback(async (collectionId) => {
    if (!collectionId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPublicFabricsByCollection(collectionId);
      setFabrics(dedupeFabricsById(data?.fabrics || []));
      if (data?.collection) {
        setSelectedCollection((prev) =>
          prev && String(prev._id) === String(collectionId)
            ? { ...prev, ...data.collection, fabrics: data.fabrics }
            : prev
        );
      }
    } catch (error) {
      console.error('Error loading collection fabrics:', error);
      setError(error.response?.data?.message || 'Failed to load fabrics in this collection');
      setFabrics([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (inCollectionView) return;
    prefetchPageRef.current = null;
    fetchingRef.current = false;
    fetchCollections(1, false);
  }, [fetchCollections, inCollectionView]);

  useEffect(() => {
    if (!selectedCollection?._id) return;
    loadCollectionFabrics(selectedCollection._id);
  }, [selectedCollection?._id, loadCollectionFabrics]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const nextQuery = (searchInput || '').trim();
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(prev => (prev === nextQuery ? prev : nextQuery));
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  const loadNextPage = useCallback(() => {
    if (inCollectionView || fetchingRef.current || isLoadingMore || isLoading || !hasNextPage) {
      return;
    }

    if (prefetchPageRef.current?.collections) {
      const { collections: prefetchedCollections, pagination: prefetchPagination } = prefetchPageRef.current;
      setCollections((prev) => {
        const seen = new Set(prev.map((c) => String(c._id)));
        const next = (prefetchedCollections || []).filter((c) => c?._id && !seen.has(String(c._id)));
        return [...prev, ...next];
      });
      setCurrentPage(prefetchPagination.currentPage);
      setHasNextPage(prefetchPagination.hasNextPage);
      prefetchPageRef.current = null;

      if (prefetchPagination.hasNextPage) {
        setTimeout(() => {
          const trimmed = (searchQuery || '').trim();
          const nextP = prefetchPagination.currentPage + 1;
          getAllPublicCollections(nextP, 24, trimmed)
            .then((prefetchResponse) => {
              prefetchPageRef.current = prefetchResponse;
            })
            .catch((err) => {
              console.warn('Prefetch failed:', err);
            });
        }, 500);
      }
    } else {
      fetchCollections(currentPage + 1, true);
    }
  }, [inCollectionView, hasNextPage, isLoadingMore, isLoading, currentPage, fetchCollections, searchQuery]);

  useEffect(() => {
    if (inCollectionView || !hasNextPage || isLoadingMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadNextPage();
        }
      },
      {
        rootMargin: '400px', // Increased to trigger much earlier for prefetching
        threshold: 0.01
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [inCollectionView, hasNextPage, isLoadingMore, isLoading, loadNextPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (inCollectionView || !hasNextPage || isLoadingMore || isLoading || fetchingRef.current) {
        return;
      }

      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      
      // Trigger when user is 500px from bottom
      if (scrollHeight - scrollTop - clientHeight < 500) {
        loadNextPage();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [inCollectionView, hasNextPage, isLoadingMore, isLoading, loadNextPage]);

  const handleOpenCollection = useCallback((collection) => {
    clearVisualSearch();
    setSearchInput('');
    setSearchQuery('');
    setFilterDraft(INITIAL_FILTERS);
    setFilters(INITIAL_FILTERS);
    setShowFilters(false);
    setSelectedCollection(collection);
    setFabrics([]);
    pushMarketplaceHistory('collection', { collectionId: collection._id });
  }, [clearVisualSearch]);

  const handleBackToCollections = useCallback(() => {
    if (isHandlingPopState.current) {
      isHandlingPopState.current = false;
      return;
    }
    setSelectedCollection(null);
    setFabrics([]);
    setSearchInput('');
    setSearchQuery('');
    const subView = window.history.state?.marketplaceSubView;
    if (subView === 'collection' || window.history.state?.view === 'collection-detail') {
      window.history.back();
    }
  }, []);

  /** Full-size / canonical URL (favourites, fallbacks). Same resolution order as MyProductsView. */
  const getFabricImage = (fabric) => {
    return (
      fabric.image ||
      fabric.images?.original?.s3Url ||
      fabric.images?.original?.imgbbUrl ||
      fabric.images?.original?.url ||
      'https://via.placeholder.com/300x300?text=No+Image'
    );
  };

  const getFabricThumbnailAndOriginal = (fabric) => {
    const originalUrl = getFabricImage(fabric);
    return { thumbnailUrl: toThumbnailUrl(originalUrl), originalUrl };
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!token;
  };

  // Favourites management functions
  const getFavourites = () => {
    try {
      const favourites = localStorage.getItem('fabricFavourites');
      return favourites ? JSON.parse(favourites) : [];
    } catch (error) {
      console.error('Error reading favourites from localStorage:', error);
      return [];
    }
  };

  const addToFavourites = (fabric, e) => {
    e.stopPropagation(); // Prevent card click
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
        onOpenLoginModal();
      } else {
        if (showNotification) {
          showNotification('Please login to add fabrics to favourites', 'info');
        }
      }
      return;
    }
    
    try {
      const favourites = getFavourites();
      const existingItemIndex = favourites.findIndex(item => item._id === fabric._id);
      
      if (existingItemIndex >= 0) {
        // Item already in favourites, remove it (toggle off)
        favourites.splice(existingItemIndex, 1);
        localStorage.setItem('fabricFavourites', JSON.stringify(favourites));
        if (showNotification) {
          showNotification('Fabric removed from favourites', 'success');
        }
      } else {
        // Add new item to favourites
        favourites.push({
          _id: fabric._id,
          name: fabric.name,
          image: getFabricImage(fabric),
          price: fabric.price || null,
          category: fabric.category,
          addedAt: new Date().toISOString()
        });
        localStorage.setItem('fabricFavourites', JSON.stringify(favourites));
        if (showNotification) {
          showNotification('Fabric added to favourites!', 'success');
        }
      }
    } catch (error) {
      console.error('Error managing favourites:', error);
      if (showNotification) {
        showNotification('Failed to update favourites', 'error');
      }
    }
  };

  const isInFavourites = (fabricId) => {
    const favourites = getFavourites();
    return favourites.some(item => item._id === fabricId);
  };

  const applyFilters = () => {
    setFilters(filterDraft);
    setCurrentPage(1);
    prefetchPageRef.current = null;
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilterDraft(INITIAL_FILTERS);
    setFilters(INITIAL_FILTERS);
    setCurrentPage(1);
    prefetchPageRef.current = null;
    setShowFilters(false);
  };

  // Handle opening fabric detail and push history state (hydrate via public/by-id so analysis + fields match My Fabrics detail)
  const handleOpenFabric = useCallback((fabric) => {
    setSelectedFabric(fabric);
    pushMarketplaceHistory('fabric', { fabricId: fabric._id });
    const id = fabric?._id ?? fabric?.id;
    if (!id) return;
    getPublicFabricById(String(id))
      .then((full) => {
        if (!full) return;
        setSelectedFabric((prev) => {
          if (!prev) return prev;
          const prevId = prev._id ?? prev.id;
          const fullId = full._id ?? full.id;
          if (String(prevId) !== String(fullId)) return prev;
          return { ...prev, ...full };
        });
      })
      .catch(() => {});
  }, []);

  // Handle closing fabric detail
  const handleCloseFabric = useCallback(() => {
    if (isHandlingPopState.current) {
      isHandlingPopState.current = false;
      return;
    }
    setSelectedFabric(null);
    // Only go back if there's history
    const subView = window.history.state?.marketplaceSubView;
    if (subView === 'fabric' || window.history.state?.view === 'fabric-detail') {
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (selectedFabric) {
        isHandlingPopState.current = true;
        setSelectedFabric(null);
        return;
      }
      if (selectedCollection) {
        isHandlingPopState.current = true;
        setSelectedCollection(null);
        setFabrics([]);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedFabric, selectedCollection]);

  useEffect(() => {
    let id;
    try {
      id = sessionStorage.getItem(SESSION_OPEN_PUBLIC_FABRIC_ID);
      if (id) sessionStorage.removeItem(SESSION_OPEN_PUBLIC_FABRIC_ID);
    } catch (_) {}
    if (!id || !String(id).trim()) return;

    const fabricId = String(id).trim();
    (async () => {
      try {
        const collectionData = await getCollectionByFabric(fabricId);
        if (collectionData?.collection) {
          setSelectedCollection({
            _id: collectionData.collection._id,
            name: collectionData.collection.name,
            image: collectionData.collection.image
          });
          const allFabrics = dedupeFabricsById([
            ...(collectionData.fabrics || []),
            ...(collectionData.collection.fabrics || [])
          ]);
          const full = await getPublicFabricById(fabricId).catch(() => null);
          if (full) {
            const merged = dedupeFabricsById([full, ...allFabrics]);
            setFabrics(merged);
          } else {
            setFabrics(allFabrics);
          }
        }
      } catch (_) {
        /* open fabric without collection context */
      }
      handleOpenFabric({ _id: fabricId });
    })();
  }, [handleOpenFabric]);

  return (
    <>
    <div
      className="min-h-screen bg-gradient-to-br from-violet-50 via-indigo-50/70 to-pink-50"
      style={{ colorScheme: 'light' }}
    >
      {/* Full Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md text-gray-900 shadow-sm border-b border-gray-200">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Row */}
          <div className="flex items-center justify-between py-3 sm:py-4 gap-3">
            <button
              onClick={inCollectionView ? handleBackToCollections : () => setActiveView('home')}
              className="p-2.5 hover:bg-gray-100 rounded-xl transition-all hover:scale-105 active:scale-95 shrink-0"
              title={inCollectionView ? 'Back to collections' : 'Go back'}
            >
              <ArrowLeft size={20} className="sm:w-5 sm:h-5" />
            </button>
            <div className="flex-1 min-w-0 text-center">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                {inCollectionView ? selectedCollection?.name || 'Collection' : 'Fabric Marketplace'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {inCollectionView
                  ? `${fabrics.length} fabric${fabrics.length === 1 ? '' : 's'} in this collection`
                  : 'Browse fabric collections from vendors'}
              </p>
            </div>
            <div className="w-10 shrink-0" aria-hidden />
          </div>

          {/* Search + Filters */}
          <div className="pb-4 sm:pb-6 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
              <div className="relative flex-1 max-w-2xl">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder={
                    inCollectionView
                      ? 'Search fabrics in this collection...'
                      : 'Search collections by name, tags, or properties...'
                  }
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  disabled={Boolean(visualSearchImageUrl)}
                  className="w-full pl-10 sm:pl-12 pr-10 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-xl text-sm sm:text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400 shadow-sm disabled:opacity-60"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-xl transition-colors"
                    aria-label="Clear search"
                  >
                    <X size={16} className="text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </div>
            {inCollectionView && visualSearchImageUrl && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-white">
                  {visualSearchImagePreview ? (
                    <img src={visualSearchImagePreview} alt="Visual search" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={14} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-600 font-medium">Searching by image similarity</span>
                <button
                  type="button"
                  onClick={clearVisualSearch}
                  className="px-2 py-1 text-xs rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                >
                  Clear image
                </button>
              </div>
            )}
            {inCollectionView && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setShowFilters(v => !v)}
                className="px-3.5 py-2 rounded-xl bg-white text-gray-800 text-sm font-semibold border border-gray-300 shadow-sm"
              >
                Filters{appliedFilterCount ? ` (${appliedFilterCount})` : ''}
              </button>
              {appliedFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-3.5 py-2 rounded-xl bg-white text-gray-700 text-sm border border-gray-300"
                >
                  Clear
                </button>
              )}
            </div>
            )}

            {inCollectionView && showFilters && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-md">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Fabric type</label>
                    <select
                      value={filterDraft.fabricType}
                      onChange={(e) => setFilterDraft(prev => ({ ...prev, fabricType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-800"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    >
                      {['', ...filterOptions.fabricTypes].map((opt) => (
                        <option key={opt || 'any'} value={opt}>
                          {opt || 'Any fabric type'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Weave / Knit</label>
                    <select
                      value={filterDraft.weaveOrKnit}
                      onChange={(e) => setFilterDraft(prev => ({ ...prev, weaveOrKnit: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-800"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    >
                      {['', ...filterOptions.weaveKnit].map((opt) => (
                        <option key={opt || 'any-weave'} value={opt}>
                          {opt || 'Any weave/knit'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Opacity</label>
                    <select
                      value={filterDraft.opacity}
                      onChange={(e) => setFilterDraft(prev => ({ ...prev, opacity: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-800"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    >
                      {['', ...filterOptions.opacity].map((opt) => (
                        <option key={opt || 'any-opacity'} value={opt}>
                          {opt || 'Any opacity'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Stretch</label>
                    <select
                      value={filterDraft.stretch}
                      onChange={(e) => setFilterDraft(prev => ({ ...prev, stretch: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-800"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    >
                      {['', ...filterOptions.stretch].map((opt) => (
                        <option key={opt || 'any-stretch'} value={opt}>
                          {opt || 'Any stretch'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Color</label>
                      <div className="flex flex-wrap gap-2">
                      {filterOptions.colors.map((colorValue) => {
                        const active = filterDraft.color === colorValue;
                        const swatchHex = getColorHex(colorValue) || '#ffffff';
                        return (
                          <button
                            key={colorValue}
                            type="button"
                            onClick={() => setFilterDraft(prev => ({ ...prev, color: active ? '' : colorValue }))}
                            className={`w-7 h-7 rounded-full border-2 ${active ? 'border-purple-600 ring-2 ring-purple-200' : 'border-gray-200'}`}
                            style={{ backgroundColor: swatchHex }}
                            title={colorValue}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowFilters(false)}
                    className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 text-sm"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-10">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 sm:py-20">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
                <Layers size={20} className="text-white sm:w-6 sm:h-6" />
              </div>
              <p className="text-sm sm:text-base text-blue-600 font-medium">
                {inCollectionView ? 'Loading fabrics...' : 'Loading collections...'}
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
          </div>
        ) : !inCollectionView ? (
          collections.length === 0 ? (
            <div className="text-center py-20">
              <Layers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                {searchQuery ? 'No collections found matching your search.' : 'No public collections available yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 sm:mb-5">
                <p className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-800">{collections.length}</span> collections
                  {searchQuery ? (
                    <>
                      {' '}for <span className="font-semibold text-purple-700">"{searchQuery}"</span>
                    </>
                  ) : null}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {collections.map((collection) => {
                  const collectionImage = getCollectionImage(collection);
                  const fabricCount = getCollectionFabricCount(collection);

                  return (
                    <div
                      key={collection._id}
                      className="group bg-white rounded-2xl border border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                      onClick={() => handleOpenCollection(collection)}
                    >
                      <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                        {collectionImage ? (
                          <LazyImage
                            src={toThumbnailUrl(collectionImage)}
                            fallbackSrc={collectionImage}
                            alt={collection.name}
                            className="w-full h-full object-cover md:group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Layers className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        {fabricCount > 0 && (
                          <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg">
                            {fabricCount} {fabricCount === 1 ? 'fabric' : 'fabrics'}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{collection.name}</h3>
                        {collection.user && (
                          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                            <User size={14} className="shrink-0" />
                            <span className="line-clamp-1">{collection.user.name || 'Vendor'}</span>
                          </div>
                        )}
                        {collection.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {collection.tags.slice(0, 3).map((tag, idx) => (
                              <span
                                key={`${tag}-${idx}`}
                                className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasNextPage && (
                <div
                  ref={observerTarget}
                  className="flex justify-center items-center py-8 min-h-[120px]"
                >
                  {isLoadingMore ? (
                    <div className="flex items-center gap-2 text-purple-500">
                      <Loader2 size={24} className="animate-spin" />
                      <span className="text-sm font-medium">Loading more collections...</span>
                    </div>
                  ) : (
                    <div className="h-20 w-full" />
                  )}
                </div>
              )}
              {!hasNextPage && collections.length > 0 && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  <p>You&apos;ve reached the end of the collections</p>
                </div>
              )}
            </>
          )
        ) : displayedFabrics.length === 0 ? (
          <div className="text-center py-20">
            <Layers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">
              {searchQuery || appliedFilterCount > 0
                ? 'No fabrics in this collection match your search or filters.'
                : 'No public fabrics in this collection yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4 sm:mb-5">
              <p className="text-sm text-gray-600">
                Showing <span className="font-semibold text-gray-800">{displayedFabrics.length}</span> fabrics
                {searchQuery ? (
                  <>
                    {' '}for <span className="font-semibold text-purple-700">"{searchQuery}"</span>
                  </>
                ) : null}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5 lg:gap-6">
              {displayedFabrics.map((fabric, index) => {
                const { thumbnailUrl, originalUrl } = getFabricThumbnailAndOriginal(fabric);
                const variantCount = fabric.colorVariants ? fabric.colorVariants.length : 0;
                const stableId = getFabricStableId(fabric);

                return (
                  <div
                    key={stableId || `fabric-row-${index}`}
                    className="group bg-white rounded-2xl border border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                    onClick={() => handleOpenFabric(fabric)}
                  >
                    {/* Fabric Image */}
                    <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      <LazyImage
                        src={thumbnailUrl}
                        fallbackSrc={originalUrl}
                        alt={fabric.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      {variantCount > 0 && (
                        <div className="absolute top-2 right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg">
                          +{variantCount} variants
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                            <Eye size={16} />
                            <span>View Details</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fabric Info */}
                    <div className="p-3.5 sm:p-4 md:p-5">
                      <h3 className="font-semibold text-gray-800 mb-1.5 sm:mb-2 line-clamp-1 text-xs sm:text-sm">
                        {fabric.name}
                      </h3>
                      {Number.isFinite(fabric.visualMatchScore) && (
                        <div
                          className="mb-2 flex items-center gap-1.5 rounded-lg bg-cyan-50 border border-cyan-100 px-2 py-1.5 text-[10px] sm:text-xs font-semibold text-cyan-900"
                          title="Visual similarity (cosine)"
                        >
                          <ScanSearch size={14} className="text-cyan-600 shrink-0" aria-hidden />
                          <span className="tabular-nums">
                            {(fabric.visualMatchScore * 100).toFixed(1)}% visual match
                          </span>
                        </div>
                      )}
                      {fabric.category && (
                        <span className="inline-block px-1.5 sm:px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] sm:text-xs rounded-full font-medium mb-2 sm:mb-3">
                          {fabric.category}
                        </span>
                      )}
                      
                      {/* Add to Favourites Button */}
                      <button
                        onClick={(e) => addToFavourites(fabric, e)}
                        className={`w-full mt-1.5 sm:mt-2 py-2 sm:py-2.5 px-2 sm:px-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-200 flex items-center justify-center gap-1.5 sm:gap-2 touch-manipulation ${
                          isInFavourites(fabric._id)
                            ? 'bg-green-500 hover:bg-green-600 text-white active:bg-green-700'
                            : 'bg-purple-600 hover:bg-purple-700 text-white active:bg-purple-800'
                        }`}
                        title={isInFavourites(fabric._id) ? 'Remove from Favourites' : 'Add to Favourites'}
                      >
                        <Heart 
                          size={14} 
                          className={`sm:w-4 sm:h-4 flex-shrink-0 ${isInFavourites(fabric._id) ? 'fill-current' : ''}`} 
                        />
                        <span className="truncate hidden sm:inline">
                          {isInFavourites(fabric._id) ? 'In Favourites' : 'Add to Favourites'}
                        </span>
                        <span className="truncate sm:hidden text-[11px]">
                          {isInFavourites(fabric._id) ? 'Favourited' : 'Favourite'}
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

          </>
        )}
      </main>

      {isCameraCaptureOpen && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl overflow-hidden shadow-2xl">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800">Capture Fabric Photo</h3>
              <button
                type="button"
                onClick={closeCameraCapture}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close camera"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                <video
                  ref={cameraVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
              <canvas ref={cameraCanvasRef} className="hidden" />
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeCameraCapture}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={captureCameraPhoto}
                className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm font-medium flex items-center gap-2"
              >
                <Camera size={16} />
                Capture
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {selectedFabric ? (
      <FabricCatalogueView
        fabric={selectedFabric}
        onClose={handleCloseFabric}
        showNotification={showNotification}
        allowShadeCardUpload={false}
        setProductDetails={setProductDetails}
        setPreSelectedProduct={setPreSelectedProduct}
        setActiveView={setActiveView}
        onOpenLoginModal={onOpenLoginModal}
      />
    ) : null}
    </>
  );
};

export default PublicFabricsGalleryView;

